import { useEffect, useState } from 'react';
import { Loader2, Undo2 } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

/**
 * Devolução individual de uma medição no Ateste. Trava SOMENTE o item
 * devolvido (`statusMedicao = DEVOLVIDA`); as demais medições do mesmo termo
 * seguem o fluxo normal.
 *
 * Dado simulado por decisão de produto (ver `_dev/cenarioDeTela.ts`): não há
 * API ainda, então a "devolução" só atualiza o estado local da página.
 */
interface DevolverMedicaoModalProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  credenciadoNome: string | null;
  onConfirmar: (motivo: string) => void;
}

const MOTIVO_MINIMO = 5;

export function DevolverMedicaoModal({
  open,
  onOpenChange,
  credenciadoNome,
  onConfirmar,
}: DevolverMedicaoModalProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (open) {
      setMotivo('');
      setEnviando(false);
    }
  }, [open]);

  const motivoValido = motivo.trim().length >= MOTIVO_MINIMO;

  const handleDevolver = () => {
    if (!motivoValido) return;
    setEnviando(true);
    window.setTimeout(() => {
      onConfirmar(motivo.trim());
      setEnviando(false);
    }, 500);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      desktopMaxWidth="max-w-lg"
      title={
        <>
          <Undo2 className="h-5 w-5 text-warning" aria-hidden />
          Devolver medição
        </>
      }
      description={
        credenciadoNome
          ? `Credenciado: ${credenciadoNome}. Só este item será travado; o restante do lote segue normalmente.`
          : 'Só este item será travado; o restante do lote segue normalmente.'
      }
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleDevolver}
            disabled={enviando || !motivoValido}
          >
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Undo2 className="h-4 w-4" aria-hidden />
            )}
            Devolver
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="motivo-devolucao">
          Motivo da devolução <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="motivo-devolucao"
          rows={5}
          placeholder="Descreva o motivo (ex.: divergência de KM, glosa não considerada, anexo faltante...)"
          value={motivo}
          onChange={(evento) => setMotivo(evento.target.value)}
          className="resize-none text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Mínimo {MOTIVO_MINIMO} caracteres. Será registrado em auditoria e exibido no histórico da
          medição.
        </p>
      </div>
    </ResponsiveModal>
  );
}

export default DevolverMedicaoModal;
